import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook to calculate and maintain responsive height based on a container element
 * @param heightMultiplier - Multiplier to apply to the container height (default: 0.9)
 * @param defaultHeight - Default height to use before calculation (default: 500)
 * @returns Tuple of [height, ref] where ref should be attached to the container element
 */
export const useResponsiveHeight = <T extends HTMLElement = HTMLDivElement>(
  heightMultiplier: number = 0.9,
  defaultHeight: number = 500
): [number, React.RefObject<T>] => {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState(defaultHeight);

  useEffect(() => {
    const updateHeight = () => {
      const currentHeight = ref.current?.offsetHeight;
      if (currentHeight) {
        setHeight(currentHeight * heightMultiplier);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, [heightMultiplier]);

  return [height, ref] as const;
};
